/**
 * Value Card Component
 *
 * Displays a single value with congruence indicator, supporting tasks, and quick actions.
 */

import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Edit2, AlertTriangle, CheckCircle2, Circle, ExternalLink, ListTodo, X, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import type { Value } from '@/types/value';
import { getCongruenceLevel, getCongruenceColor } from '@/types/value';
import { useSupportingTasks, useRemoveSupportingTask } from '@/hooks/use-values';
import { useTask } from '@/hooks/use-tasks';

interface ValueCardProps {
  value: Value;
  onEdit?: (value: Value) => void;
  onRecordEvidence?: (value: Value, type: 'lived' | 'violated') => void;
  onEditTask?: (taskId: string) => void;
}

/**
 * Individual supporting task row
 */
function SupportingTaskRow({
  taskId,
  valueId,
  onEditTask,
}: {
  taskId: string;
  valueId: string;
  onEditTask?: (taskId: string) => void;
}) {
  const { data: taskData, isLoading } = useTask(taskId);
  const removeTask = useRemoveSupportingTask();
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsRemoving(true);
    try {
      await removeTask.mutateAsync({ valueId, taskId });
    } catch (error) {
      console.error('Failed to remove supporting task:', error);
    } finally {
      setIsRemoving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-stone-400 py-1">
        <Loader2 className="w-3 h-3 animate-spin" />
        Loading...
      </div>
    );
  }

  const task = taskData?.task;
  if (!task) return null;

  return (
    <div className="flex items-center justify-between gap-2 py-1 px-2 rounded hover:bg-stone-50 group">
      <div className="flex-1 min-w-0">
        <button
          onClick={() => onEditTask?.(taskId)}
          className="text-xs text-stone-700 hover:text-primary-600 truncate block text-left w-full"
          title={task.title}
        >
          {task.title}
        </button>
      </div>
      <button
        onClick={handleRemove}
        disabled={isRemoving}
        className="p-0.5 text-stone-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
        title="Remove from this value"
      >
        {isRemoving ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <X className="w-3 h-3" />
        )}
      </button>
    </div>
  );
}

export function ValueCard({ value, onEdit, onRecordEvidence, onEditTask }: ValueCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  const congruenceLevel = getCongruenceLevel(value.congruenceScore);
  const colors = getCongruenceColor(value.congruenceScore);
  const scorePercent = Math.round(value.congruenceScore * 100);
  
  // Fetch supporting tasks
  const { data: supportingTasksData, isLoading: isLoadingTasks } = useSupportingTasks(value.id);
  const supportingTasks = supportingTasksData?.supportingTasks || [];
  const hasSuportingTasks = supportingTasks.length > 0;

  const levelIcon = {
    strong: <CheckCircle2 className="w-5 h-5 text-green-600" />,
    partial: <Circle className="w-5 h-5 text-yellow-600" />,
    gap: <AlertTriangle className="w-5 h-5 text-red-600" />,
  };

  const levelLabel = {
    strong: 'Strong',
    partial: 'Partial',
    gap: 'Gap',
  };

  return (
    <div
      data-testid="value-card"
      className={`
        relative bg-white rounded-xl border-l-4 shadow-sm transition-all duration-200
        ${colors.border}
        ${isHovered ? 'shadow-md' : ''}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="p-4">
        {/* Header: Icon, Name, Weight */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {levelIcon[congruenceLevel]}
            <div>
              <h3 className="font-semibold text-stone-900" data-testid="value-name">{value.name}</h3>
              {value.priority && (
                <span className="text-xs text-stone-500" data-testid="value-priority">Priority #{value.priority}</span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-stone-900" data-testid="value-weight">{value.percentage?.toFixed(0) || 0}%</div>
            <div className="text-xs text-stone-500">weight</div>
          </div>
        </div>

        {/* Description */}
        {value.description && (
          <p className="mt-2 text-sm text-stone-600 line-clamp-2" data-testid="value-description">{value.description}</p>
        )}

        {/* Congruence Score Bar */}
        <div className="mt-3" data-testid="value-congruence">
          <div className="flex items-center justify-between mb-1">
            <span className={`text-xs font-medium ${colors.text}`} data-testid="value-congruence-label">
              {levelLabel[congruenceLevel]} alignment
            </span>
            <span className="text-xs font-semibold text-stone-700" data-testid="value-congruence-score">{scorePercent}%</span>
          </div>
          <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
            <div
              data-testid="value-congruence-bar"
              className={`h-full rounded-full transition-all duration-500 ${colors.bg.replace('100', '500')}`}
              style={{ width: `${scorePercent}%`, backgroundColor: colors.fill }}
            />
          </div>
        </div>

        {/* Last lived */}
        {value.lastLivedAt && (
          <div className="mt-2 text-xs text-stone-500">
            Last lived: {formatDistanceToNow(new Date(value.lastLivedAt), { addSuffix: true })}
          </div>
        )}

        {/* Linked Task Warning */}
        {value.linkedTaskId && congruenceLevel === 'gap' && (
          <div className="mt-3 flex items-center gap-2 p-2 bg-amber-50 rounded-lg text-xs text-amber-700">
            <AlertTriangle className="w-4 h-4" />
            <span>Task created for this neglected value</span>
            <ExternalLink className="w-3 h-3 ml-auto" />
          </div>
        )}

        {/* Supporting Tasks Section */}
        {(hasSuportingTasks || isLoadingTasks) && (
          <div className="mt-3">
            <button
              onClick={() => setShowTasks(!showTasks)}
              className="flex items-center gap-2 text-xs font-medium text-stone-600 hover:text-stone-800 w-full"
            >
              <ListTodo className="w-3.5 h-3.5" />
              <span>
                {isLoadingTasks ? 'Loading tasks...' : `${supportingTasks.length} supporting task${supportingTasks.length !== 1 ? 's' : ''}`}
              </span>
              {showTasks ? (
                <ChevronUp className="w-3 h-3 ml-auto" />
              ) : (
                <ChevronDown className="w-3 h-3 ml-auto" />
              )}
            </button>
            
            {showTasks && !isLoadingTasks && (
              <div className="mt-2 border border-stone-100 rounded-lg divide-y divide-stone-100">
                {supportingTasks.map((st) => (
                  <SupportingTaskRow
                    key={st.edgeId}
                    taskId={st.taskId}
                    valueId={value.id}
                    onEditTask={onEditTask}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Quick Actions (on hover) */}
        {isHovered && (
          <div className="mt-3 pt-3 border-t border-stone-100 flex items-center gap-2" data-testid="value-actions">
            {onEdit && (
              <button
                data-testid="edit-value-button"
                onClick={() => onEdit(value)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors"
              >
                <Edit2 className="w-3 h-3" />
                Edit
              </button>
            )}
            {onRecordEvidence && (
              <>
                <button
                  data-testid="lived-value-button"
                  onClick={() => onRecordEvidence(value, 'lived')}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                >
                  <CheckCircle2 className="w-3 h-3" />
                  Lived it
                </button>
                <button
                  data-testid="violated-value-button"
                  onClick={() => onRecordEvidence(value, 'violated')}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                >
                  <AlertTriangle className="w-3 h-3" />
                  Violated
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

